import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

// Import Analytics components (lazy-loaded for performance)
const AnalyticsDashboard = React.lazy(() => import('./AnalyticsDashboard'));
const AnalyticsPages = React.lazy(() => import('./AnalyticsPages'));
const AnalyticsRealTime = React.lazy(() => import('./AnalyticsRealTime'));
const AnalyticsCalculators = React.lazy(() => import('./AnalyticsCalculators'));
const AnalyticsWhatsNew = React.lazy(() => import('./AnalyticsWhatsNew'));
const NotificationSettings = React.lazy(() => import('./NotificationSettings'));

const analyticsModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: 'analytics',
  name: 'Analytics',
  description: 'Privacy-first website analytics with real-time visitor tracking',
  version: '1.0.0',
  enabled: true,
  category: 'marketing',
  order: 16,
  icon: '📊',
  color: '#3182CE', // Blue

  // ROUTES - permissionLevel is resolved from backend at runtime
  routes: [
    {
      path: '/analytics',
      component: AnalyticsDashboard,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/analytics/pages',
      component: AnalyticsPages,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/analytics/realtime',
      component: AnalyticsRealTime,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/analytics/calculators',
      component: AnalyticsCalculators,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/analytics/whats-new',
      component: AnalyticsWhatsNew,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/analytics/notification-settings',
      component: NotificationSettings,
      permissionLevel: 'edit',
      useLayout: true,
    },
  ],

  // NAVIGATION - permissionLevel is resolved from backend at runtime
  navigation: [
    {
      label: 'Dashboard',
      path: '/analytics',
      icon: '📊',
      permissionLevel: 'view',
    },
    {
      label: 'Top Pages',
      path: '/analytics/pages',
      icon: '📄',
      permissionLevel: 'view',
    },
    {
      label: 'Real-Time',
      path: '/analytics/realtime',
      icon: '🔴',
      permissionLevel: 'view',
    },
    {
      label: 'Calculator Stats',
      path: '/analytics/calculators',
      icon: '🧮',
      permissionLevel: 'view',
    },
    {
      label: "What's New",
      path: '/analytics/whats-new',
      icon: '🎉',
      permissionLevel: 'view',
    },
    {
      label: 'Notification Rules',
      path: '/analytics/notification-settings',
      icon: '🔔',
      permissionLevel: 'edit',
    },
  ],

  // PERMISSIONS - resolved from backend single-source-of-truth
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: 'View Real-Time',
      path: '/analytics/realtime',
      icon: '🔴',
      description: 'See who is on your site right now',
    },
  ],

  // CHANGELOG
  changelog: [
    {
      version: '1.0.0',
      releaseDate: '2025-12-10',
      title: 'Initial Release - Privacy-First Analytics',
      features: [
        '📊 Analytics dashboard with key metrics (views, visitors, bounce rate)',
        '📈 Time series charts for traffic visualization',
        '📄 Top pages breakdown with engagement metrics',
        '🔴 Real-time visitor tracking (last 5 minutes)',
        '📱 Device breakdown (desktop, mobile, tablet)',
        '🌐 Browser statistics',
        '🔗 Traffic source analysis (organic, direct, referral, social)',
        '🧮 Calculator-specific analytics for lead magnets',
        '🔒 Privacy-first: IP addresses are hashed, DNT header respected',
        '📦 Automatic 1-year data retention with auto-cleanup',
        '🏢 Full multi-tenant support with data isolation',
      ],
      notes: 'First release of the Analytics module. A privacy-first alternative to Google Analytics that tracks page views, visitors, and engagement without invasive third-party cookies or external data sharing.',
    },
  ],

  // ROADMAP
  roadmap: [
    {
      title: 'Export & Reporting',
      description: 'Export analytics data to CSV/PDF and schedule automated reports',
      targetVersion: '1.1.0',
      priority: 'medium',
      status: 'planned',
      features: [
        'CSV export for all data views',
        'PDF report generation',
        'Scheduled email reports',
        'Custom date range exports',
      ],
    },
    {
      title: 'Goal Tracking',
      description: 'Set and track conversion goals',
      targetVersion: '1.2.0',
      priority: 'high',
      status: 'planned',
      features: [
        'Define custom conversion goals',
        'Goal completion tracking',
        'Funnel visualization',
        'Goal-based reporting',
      ],
    },
  ],
};

export default analyticsModuleConfig;
